import { useEffect, useRef, useState } from 'react';
import { useDialog } from '../lib/useDialog';
import { AlarmClock, Flame, Mail, Pencil, Plus, Settings, Siren, Trash2, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth, hasRole } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { RichEditor, type RichEditorHandle } from './RichEditor';

/**
 * SETTINGS DE UNA WORKING VIEW — el ⚙ que sale arriba a la derecha de cada cola
 * (COC, First Estimate, Claim in…). Es UNA sola ventana reutilizable: se le pasa
 * `deptKey` + `viewKey` y ya.
 *
 * Dos bloques, con permisos distintos a propósito:
 *   · PLANTILLAS de email — solo salen en el módulo de email de ESTA vista. Las crea y edita
 *     cualquiera; BORRARLAS es de manager para arriba.
 *   · DÍAS del follow-up y del semáforo — solo super-manager (cambian el trabajo de todos).
 *     El resto los ve, pero en gris.
 */
export type ViewSettingsData = {
  view: {
    dept_key: string; view_key: string; label: string;
    followup_mode: string; followup_days: number | null;
    urgent_days: number | null; manager_days: number | null;
    green_days: number | null; yellow_days: number | null;
    effective: { urgent_days: number; manager_days: number; green_days: number; yellow_days: number };
  };
  templates: Array<{
    id: string; name: string; subject: string | null; body_html: string;
    sort_order: number; created_by: string | null; created_at: string;
  }>;
};
type Template = ViewSettingsData['templates'][number];

/** Botón ⚙ estándar de la esquina de una cola. */
export function ViewSettingsButton({ onClick }: { onClick: () => void }) {
  const { t } = useI18n();
  return (
    <button className="btn ghost sm" onClick={onClick} title={t('vset.title')} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <Settings size={14} strokeWidth={1.75} /> {t('vset.settings')}
    </button>
  );
}

/**
 * Placeholders de plantilla: {name} {address} {contractor} {rcv} {claim} {insurance}.
 * En el editor se insertan como chips morados (igual que los del email) y al USAR la plantilla en
 * un claim se reemplazan por los datos reales de ESE claim. En el asunto se escriben como texto.
 */
export const TPL_PLACEHOLDERS = ['name', 'address', 'contractor', 'rcv', 'claim', 'insurance'] as const;
export type TplPlaceholder = (typeof TPL_PLACEHOLDERS)[number];
const PH_LABEL_KEY: Record<TplPlaceholder, string> = {
  name: 'em.tName', address: 'em.tAddress', contractor: 'em.tContractor',
  rcv: 'em.tRcv', claim: 'em.tClaim', insurance: 'em.tInsurance',
};
export function placeholderChipHtml(ph: TplPlaceholder, label: string): string {
  return `<span contenteditable="false" data-ph="${ph}" style="border:1px dashed #a855f7;border-radius:5px;padding:0 5px;margin:0 1px;color:#7c3aed;background:#f5f3ff">{${label}}</span>&nbsp;`;
}

function TemplateEditor({ initial, onCancel, onSave }: {
  initial: Partial<Template>;
  onCancel: () => void;
  onSave: (v: { name: string; subject: string | null; bodyHtml: string }) => Promise<void>;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(initial.name ?? '');
  const [subject, setSubject] = useState(initial.subject ?? '');
  const [busy, setBusy] = useState(false);
  const ed = useRef<RichEditorHandle>(null);

  return (
    <div style={{ display: 'grid', gap: 6, border: '1px solid var(--line)', borderRadius: 8, padding: 8 }}>
      <input className="input sm" placeholder={t('vset.tplName')} value={name} onChange={(e) => setName(e.target.value)} />
      <input className="input sm" placeholder={t('vset.tplSubject')} value={subject} onChange={(e) => setSubject(e.target.value)} />
      {/* Etiquetas de datos (las mismas del módulo de email): acá insertan un PLACEHOLDER que se
          rellena con los datos del claim cuando alguien usa la plantilla. */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {TPL_PLACEHOLDERS.map((ph) => (
          <button key={ph} type="button" className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', borderColor: '#a855f7', color: '#7c3aed' }}
            onClick={() => ed.current?.insertHtml(placeholderChipHtml(ph, t(PH_LABEL_KEY[ph])))}>
            + {t(PH_LABEL_KEY[ph])}
          </button>
        ))}
      </div>
      <RichEditor ref={ed} minHeight={90} placeholder={t('vset.tplBody')} initialHtml={initial.body_html ?? ''} />
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          className="btn sm"
          disabled={busy || !name.trim()}
          onClick={async () => {
            setBusy(true);
            try { await onSave({ name: name.trim(), subject: subject.trim() || null, bodyHtml: ed.current?.getHtml() ?? '' }); }
            finally { setBusy(false); }
          }}
        >
          {busy ? '…' : t('common.save')}
        </button>
        <button className="btn ghost sm" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}

export function ViewSettingsModal({ deptKey, viewKey, label, onClose }: {
  deptKey: string; viewKey: string; label?: string; onClose: () => void;
}) {
  const { t } = useI18n();
  const dialogo = useDialog(onClose);
  const { me } = useAuth();
  const canDelete = hasRole(me, 'manager');
  const canEditDays = hasRole(me, 'super-manager');

  const [data, setData] = useState<ViewSettingsData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Borradores de los días: se editan libres y se guardan con el botón (no en cada tecla).
  const [mode, setMode] = useState('overdue');
  const [days, setDays] = useState('');
  const [urgent, setUrgent] = useState('');
  const [manager, setManager] = useState('');
  const [green, setGreen] = useState('');
  const [yellow, setYellow] = useState('');

  const apply = (d: ViewSettingsData) => {
    setData(d);
    setMode(d.view.followup_mode);
    setDays(d.view.followup_days != null ? String(d.view.followup_days) : '');
    setUrgent(d.view.urgent_days != null ? String(d.view.urgent_days) : '');
    setManager(d.view.manager_days != null ? String(d.view.manager_days) : '');
    setGreen(d.view.green_days != null ? String(d.view.green_days) : '');
    setYellow(d.view.yellow_days != null ? String(d.view.yellow_days) : '');
  };

  useEffect(() => {
    let alive = true;
    api.get<ViewSettingsData>(`/api/views/${encodeURIComponent(deptKey)}/${encodeURIComponent(viewKey)}/settings`)
      .then((d) => { if (alive) apply(d); })
      .catch((e) => {
        if (!alive) return;
        // La vista existe en el menú pero no como fila configurable (colas viejas hardcodeadas).
        setErr(e instanceof ApiError && e.message === 'view_not_found' ? t('vset.errNoView') : t('vset.errSave'));
      });
    return () => { alive = false; };
  }, [deptKey, viewKey]);

  const base = `/api/views/${encodeURIComponent(deptKey)}/${encodeURIComponent(viewKey)}`;
  const setTemplates = (templates: Template[]) => setData((d) => (d ? { ...d, templates } : d));

  async function saveDays() {
    setBusy(true); setErr(null); setMsg(null);
    const n = (s: string) => (s.trim() === '' ? null : Number(s));
    try {
      const r = await api.patch<{ view: ViewSettingsData['view'] }>(`${base}/settings`, {
        followupMode: mode,
        followupDays: n(days),
        urgentDays: n(urgent),
        managerDays: n(manager),
        greenDays: n(green),
        yellowDays: n(yellow),
      });
      setData((d) => (d ? { ...d, view: r.view } : d));
      setMsg(t('common.saved'));
    } catch (e) {
      setErr(
        e instanceof ApiError && e.message === 'manager_days_before_urgent' ? t('vset.errManagerBeforeUrgent')
        : e instanceof ApiError && e.message === 'yellow_days_before_green' ? t('vset.errYellowBeforeGreen')
        : t('vset.errSave'),
      );
    } finally { setBusy(false); }
  }

  const hasFollowUp = mode === 'overdue' || mode === 'calendar';

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div className="card" style={{ width: 620, maxWidth: '94vw', maxHeight: '86vh', overflowY: 'auto', padding: 0 }} onClick={(e) => e.stopPropagation()} ref={dialogo.ref} {...dialogo.props}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--line)', position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1 }}>
          <b style={{ flex: 1 }}><Settings size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('vset.title')} — {data?.view.label ?? label ?? viewKey}</b>
          <button className="calx-pop-x" onClick={onClose}><X size={14} strokeWidth={1.75} /></button>
        </div>

        {err && !data ? (
          <div style={{ padding: 16, fontSize: 'var(--fs-sm)' }}><span className="badge red">{err}</span></div>
        ) : !data ? (
          <div className="muted" style={{ padding: 16, fontSize: 'var(--fs-sm)' }}>{t('common.loadingDots')}</div>
        ) : (
          <div style={{ padding: 14, display: 'grid', gap: 18 }}>
            {/* ── PLANTILLAS DE EMAIL ─────────────────────────────────────────── */}
            <section style={{ display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <b style={{ fontSize: 'var(--fs-sm)' }}><Mail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('vset.templates')}</b>
                <span className="badge gray">{data.templates.length}</span>
                <span style={{ flex: 1 }} />
                {!adding && <button className="btn sm" onClick={() => { setAdding(true); setEditing(null); }}><Plus size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('vset.tplNew')}</button>}
              </div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('vset.templatesHelp')}</div>

              {adding && (
                <TemplateEditor
                  initial={{}}
                  onCancel={() => setAdding(false)}
                  onSave={async (v) => {
                    const r = await api.post<{ templates: Template[] }>(`${base}/templates`, v);
                    setTemplates(r.templates); setAdding(false);
                  }}
                />
              )}

              {data.templates.length === 0 && !adding ? (
                <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('vset.tplNone')}</div>
              ) : (
                <div style={{ display: 'grid', gap: 6 }}>
                  {data.templates.map((tpl) => (
                    editing === tpl.id ? (
                      <TemplateEditor
                        key={tpl.id}
                        initial={tpl}
                        onCancel={() => setEditing(null)}
                        onSave={async (v) => {
                          const r = await api.patch<{ templates: Template[] }>(`/api/views/templates/${tpl.id}`, v);
                          setTemplates(r.templates); setEditing(null);
                        }}
                      />
                    ) : (
                      <div key={tpl.id} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--line)', borderRadius: 8, padding: '7px 9px' }}>
                        <span style={{ display: 'grid', gap: 1, flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.name}</span>
                          <span className="muted" style={{ fontSize: 'var(--fs-2xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {tpl.subject || t('vset.tplNoSubject')}{tpl.created_by ? ` · ${tpl.created_by}` : ''}
                          </span>
                        </span>
                        <button className="btn ghost sm" onClick={() => { setEditing(tpl.id); setAdding(false); }}><Pencil size={13} strokeWidth={1.75} /></button>
                        {canDelete && (
                          <button
                            className="btn ghost sm"
                            style={{ color: 'var(--red)' }}
                            onClick={async () => {
                              if (!window.confirm(t('vset.tplDeleteConfirm', { name: tpl.name }))) return;
                              try {
                                const r = await api.del<{ templates: Template[] }>(`/api/views/templates/${tpl.id}`);
                                setTemplates(r.templates);
                              } catch { setErr(t('vset.errSave')); }
                            }}
                          >
                            <Trash2 size={13} strokeWidth={1.75} />
                          </button>
                        )}
                      </div>
                    )
                  ))}
                </div>
              )}
              {!canDelete && data.templates.length > 0 && (
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('vset.tplDeleteManagerOnly')}</div>
              )}
            </section>

            {/* ── FOLLOW-UP Y SEMÁFORO ────────────────────────────────────────── */}
            <section style={{ display: 'grid', gap: 8, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <b style={{ fontSize: 'var(--fs-sm)' }}><AlarmClock size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('vset.followUp')}</b>
                {!canEditDays && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t('vset.superManagerOnly')}</span>}
              </div>

              <label className="field" style={{ fontSize: 'var(--fs-xs)' }}>
                {t('vset.fuMode')}
                <select className="input sm" value={mode} disabled={!canEditDays} onChange={(e) => setMode(e.target.value)}>
                  <option value="overdue">{t('vset.fuOverdue')}</option>
                  <option value="calendar">{t('vset.fuCalendar')}</option>
                  <option value="days">{t('vset.fuDays')}</option>
                  <option value="none">{t('vset.fuNone')}</option>
                </select>
              </label>

              {/* 2026-08-09: en modo 'overdue' los días de la VISTA también mandan (motor único
                  de follow-up) — el campo se muestra en los tres modos con follow-up/tope. */}
              {(mode === 'calendar' || mode === 'days' || mode === 'overdue') && (
                <label className="field" style={{ fontSize: 'var(--fs-xs)' }}>
                  {mode === 'days' ? t('vset.fuDaysMax') : t('vset.fuEveryDays')}
                  <input className="input sm" type="number" min={1} max={365} style={{ width: 110 }} value={days} disabled={!canEditDays} onChange={(e) => setDays(e.target.value)} />
                </label>
              )}

              {hasFollowUp ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 10 }}>
                  <label className="field" style={{ fontSize: 'var(--fs-xs)' }}>
                    <span><Flame size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('vset.urgentAfter')}</span>
                    <input className="input sm" type="number" min={0} max={365} placeholder={String(data.view.effective.urgent_days)}
                      value={urgent} disabled={!canEditDays} onChange={(e) => setUrgent(e.target.value)} />
                    <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('vset.blankDefault', { n: String(data.view.effective.urgent_days) })}</span>
                  </label>
                  <label className="field" style={{ fontSize: 'var(--fs-xs)' }}>
                    <span><Siren size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('vset.managerAfter')}</span>
                    <input className="input sm" type="number" min={0} max={365} placeholder={String(data.view.effective.manager_days)}
                      value={manager} disabled={!canEditDays} onChange={(e) => setManager(e.target.value)} />
                    <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('vset.blankDefault', { n: String(data.view.effective.manager_days) })}</span>
                  </label>
                </div>
              ) : (
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('vset.noAlertsHelp')}</div>
              )}

              {/* Color del círculo de días de la fila (sin 🔥: verde → amarillo → rojo). */}
              <div style={{ display: 'grid', gap: 6, marginTop: 4 }}>
                <b style={{ fontSize: 'var(--fs-xs)' }}>{t('vset.dayColors')}</b>
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('vset.dayColorsHelp')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 10 }}>
                  <label className="field" style={{ fontSize: 'var(--fs-xs)' }}>
                    <span><span className="dot green" style={{ marginRight: 4 }} /> {t('vset.greenUpTo')}</span>
                    <input className="input sm" type="number" min={0} max={365} placeholder={String(data.view.effective.green_days)}
                      value={green} disabled={!canEditDays} onChange={(e) => setGreen(e.target.value)} />
                    <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('vset.blankDefault', { n: String(data.view.effective.green_days) })}</span>
                  </label>
                  <label className="field" style={{ fontSize: 'var(--fs-xs)' }}>
                    <span><span className="dot orange" style={{ marginRight: 4 }} /> {t('vset.yellowUpTo')}</span>
                    <input className="input sm" type="number" min={0} max={365} placeholder={String(data.view.effective.yellow_days)}
                      value={yellow} disabled={!canEditDays} onChange={(e) => setYellow(e.target.value)} />
                    <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('vset.blankDefault', { n: String(data.view.effective.yellow_days) })}</span>
                  </label>
                </div>
              </div>

              {canEditDays && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button className="btn sm" disabled={busy} onClick={saveDays}>{busy ? '…' : t('common.save')}</button>
                  {msg && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{msg}</span>}
                </div>
              )}
            </section>

            {err && <div className="badge red" style={{ alignSelf: 'flex-start' }}>{err}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
